import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="post-settings-bulk"
// スケジュール一覧の一括有効化・停止
// - 行のチェックボックス（data-status / data-word-press-stopped を持つ）の選択状態から、選択件数・ボタンの disabled・
//   全選択の状態と、確認モーダル（PostSettings::BulkStatusModal::Component）の件数・警告を更新する
// - 対象外の状態のものも含めて選択中の ID をすべて送る（スキップの判定はサーバー側で行う）
export default class extends Controller {
  static targets = [
    "checkbox", "selectAll", "selectedCount", "actionButton", "idsContainer",
    "restoreCount", "restoreSkippedNote", "restoreSkippedCount", "restoreSubmit", "stoppedWarning", "stoppedCount",
    "stopCount", "stopSkippedNote", "stopSkippedCount", "stopSubmit",
  ]

  declare readonly checkboxTargets: HTMLInputElement[]
  declare readonly selectAllTargets: HTMLInputElement[]
  declare readonly selectedCountTarget: HTMLElement
  declare readonly actionButtonTargets: HTMLButtonElement[]
  declare readonly idsContainerTargets: HTMLElement[]
  declare readonly restoreCountTargets: HTMLElement[]
  declare readonly restoreSkippedNoteTargets: HTMLElement[]
  declare readonly restoreSkippedCountTargets: HTMLElement[]
  declare readonly restoreSubmitTargets: HTMLInputElement[]
  declare readonly stoppedWarningTargets: HTMLElement[]
  declare readonly stoppedCountTargets: HTMLElement[]
  declare readonly stopCountTargets: HTMLElement[]
  declare readonly stopSkippedNoteTargets: HTMLElement[]
  declare readonly stopSkippedCountTargets: HTMLElement[]
  declare readonly stopSubmitTargets: HTMLInputElement[]

  private updateScheduled = false

  // 絞り込みで一覧（turbo frame）が差し替わったときも表示を合わせる。
  // 行ごとに通知されるため、全行を走査する update() は同じ DOM 更新に対して 1 回にまとめる
  checkboxTargetConnected(): void {
    this.scheduleUpdate()
  }

  checkboxTargetDisconnected(): void {
    this.scheduleUpdate()
  }

  toggleAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked
    this.checkboxTargets.forEach(checkbox => { checkbox.checked = checked })
    this.update()
  }

  update(): void {
    const selected = this.selectedCheckboxes
    const total = this.checkboxTargets.length

    this.selectedCountTarget.textContent = String(selected.length)
    this.actionButtonTargets.forEach(button => { button.disabled = selected.length === 0 })
    this.selectAllTargets.forEach(selectAll => {
      selectAll.checked = total > 0 && selected.length === total
      selectAll.indeterminate = selected.length > 0 && selected.length < total
    })

    const restoreTargets = selected.filter(checkbox => checkbox.dataset.status === "inactive")
    const stopTargets = selected.filter(checkbox => checkbox.dataset.status === "active")
    // 警告は有効化の対象になるもの（停止中）のうち、WordPress連携が停止中のサイトのものだけを数える
    const stoppedCount = restoreTargets.filter(checkbox => checkbox.dataset.wordPressStopped === "true").length

    this.renderCounts(restoreTargets.length, selected.length - restoreTargets.length,
      this.restoreCountTargets, this.restoreSkippedNoteTargets, this.restoreSkippedCountTargets, this.restoreSubmitTargets)
    this.renderCounts(stopTargets.length, selected.length - stopTargets.length,
      this.stopCountTargets, this.stopSkippedNoteTargets, this.stopSkippedCountTargets, this.stopSubmitTargets)
    this.stoppedCountTargets.forEach(element => { element.textContent = String(stoppedCount) })
    this.stoppedWarningTargets.forEach(element => element.classList.toggle("d-none", stoppedCount === 0))
  }

  // 送信するフォームに、選択中の ID を hidden（ids[]）として差し込む
  fillIds(event: Event): void {
    const form = event.currentTarget as HTMLFormElement
    const container = this.idsContainerTargets.find(element => form.contains(element))
    if (!container) return

    container.replaceChildren(...this.selectedCheckboxes.map(checkbox => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "ids[]"
      input.value = checkbox.value
      return input
    }))
  }

  private scheduleUpdate(): void {
    if (this.updateScheduled) return

    this.updateScheduled = true
    queueMicrotask(() => {
      this.updateScheduled = false
      // まとめて実行する前に、ページ遷移などでコントローラーが外れていれば何もしない
      if (this.element.isConnected) this.update()
    })
  }

  private get selectedCheckboxes(): HTMLInputElement[] {
    return this.checkboxTargets.filter(checkbox => checkbox.checked)
  }

  // 対象が 0 件のときは送信しても何も更新されないため、「はい」を押せないようにする
  private renderCounts(targetCount: number, skippedCount: number, countTargets: HTMLElement[],
    skippedNoteTargets: HTMLElement[], skippedCountTargets: HTMLElement[], submitTargets: HTMLInputElement[]): void {
    countTargets.forEach(element => { element.textContent = String(targetCount) })
    skippedCountTargets.forEach(element => { element.textContent = String(skippedCount) })
    skippedNoteTargets.forEach(element => element.classList.toggle("d-none", skippedCount === 0))
    submitTargets.forEach(submit => { submit.disabled = targetCount === 0 })
  }
}
